import { PortfolioPiece } from "@/lib/contract/schemas";
import { allPlatformCreators } from "@/lib/contract/fixtures";
import { getCurrentUser } from "./auth";

const STORAGE_PREFIX = "retake_portfolio_";

function getStorageKey(idOrHandle: string): string {
  return `${STORAGE_PREFIX}${idOrHandle}`;
}

/**
 * Normalizes an identifier or handle to find matching fixture data.
 */
function findFixtureCreator(idOrHandle: string) {
  const norm = idOrHandle.toLowerCase().replace(/\./g, "-");
  return (
    allPlatformCreators.find(
      (c) =>
        c.id.toLowerCase() === idOrHandle.toLowerCase() ||
        c.handle.toLowerCase() === idOrHandle.toLowerCase() ||
        c.handle.toLowerCase() === norm ||
        c.id.toLowerCase() === norm
    ) || null
  );
}

/**
 * Retrieves portfolio for a given creator ID or handle.
 * Checks localStorage first, and falls back to fixture defaults.
 */
export function getCreatorPortfolio(creatorIdOrHandle?: string | null): PortfolioPiece[] {
  if (typeof window === "undefined") {
    if (creatorIdOrHandle) {
      const match = findFixtureCreator(creatorIdOrHandle);
      if (match) return match.portfolio;
    }
    const arjun = allPlatformCreators.find((c) => c.id === "creator-arjun");
    return arjun?.portfolio ?? [];
  }

  const activeId = creatorIdOrHandle || getCurrentUser()?.id || "creator-arjun";
  const matchedFixture = findFixtureCreator(activeId);

  // 1. Try direct lookup by activeId
  try {
    const raw = localStorage.getItem(getStorageKey(activeId));
    if (raw !== null) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed as PortfolioPiece[];
    }
  } catch {}

  // 2. If matched fixture exists, check by fixture id or handle
  if (matchedFixture) {
    try {
      const rawId = localStorage.getItem(getStorageKey(matchedFixture.id));
      if (rawId !== null) {
        const parsed = JSON.parse(rawId);
        if (Array.isArray(parsed)) return parsed as PortfolioPiece[];
      }
      const rawHandle = localStorage.getItem(getStorageKey(matchedFixture.handle));
      if (rawHandle !== null) {
        const parsed = JSON.parse(rawHandle);
        if (Array.isArray(parsed)) return parsed as PortfolioPiece[];
      }
    } catch {}
  }

  // 3. Check if current logged-in user has saved portfolio
  const currentUser = getCurrentUser();
  if (currentUser && currentUser.id !== activeId) {
    const userMatch = findFixtureCreator(currentUser.id);
    const isSameCreator =
      (matchedFixture && userMatch && matchedFixture.id === userMatch.id) ||
      currentUser.role === "creator";

    if (isSameCreator) {
      try {
        const rawUser = localStorage.getItem(getStorageKey(currentUser.id));
        if (rawUser !== null) {
          const parsed = JSON.parse(rawUser);
          if (Array.isArray(parsed)) return parsed as PortfolioPiece[];
        }
      } catch {}
    }
  }

  // 4. Fallback to fixture data (prefer matched creator, then arjun, then first creator)
  const fallbackCreator =
    matchedFixture ||
    allPlatformCreators.find((c) => c.id === "creator-arjun") ||
    allPlatformCreators[0];

  const defaultPieces = fallbackCreator?.portfolio ?? [];

  // Seed into localStorage so subsequent mutations persist deterministically
  try {
    localStorage.setItem(getStorageKey(activeId), JSON.stringify(defaultPieces));
    if (matchedFixture) {
      localStorage.setItem(getStorageKey(matchedFixture.id), JSON.stringify(defaultPieces));
      localStorage.setItem(getStorageKey(matchedFixture.handle), JSON.stringify(defaultPieces));
    }
  } catch {}

  return defaultPieces;
}

/**
 * Persists creator portfolio to localStorage across all relevant alias keys.
 */
export function saveCreatorPortfolio(creatorIdOrHandle: string, portfolio: PortfolioPiece[]): void {
  if (typeof window === "undefined") return;

  const dataStr = JSON.stringify(portfolio);
  const matchedFixture = findFixtureCreator(creatorIdOrHandle);

  try {
    localStorage.setItem(getStorageKey(creatorIdOrHandle), dataStr);

    if (matchedFixture) {
      localStorage.setItem(getStorageKey(matchedFixture.id), dataStr);
      localStorage.setItem(getStorageKey(matchedFixture.handle), dataStr);
    }

    const currentUser = getCurrentUser();
    if (currentUser?.id) {
      if (
        creatorIdOrHandle === currentUser.id ||
        (matchedFixture && (matchedFixture.id === currentUser.id || matchedFixture.handle === currentUser.id)) ||
        currentUser.role === "creator"
      ) {
        localStorage.setItem(getStorageKey(currentUser.id), dataStr);
      }
    }
  } catch {
    // Ignore storage quota or restricted access errors
  }
}

/**
 * Adds a new portfolio piece to the creator's portfolio and persists it.
 */
export function addCreatorPortfolioPiece(creatorIdOrHandle: string, piece: PortfolioPiece): PortfolioPiece[] {
  const current = getCreatorPortfolio(creatorIdOrHandle);
  const updated = [piece, ...current.filter((p) => p.id !== piece.id)];
  saveCreatorPortfolio(creatorIdOrHandle, updated);
  return updated;
}

/**
 * Updates an existing portfolio piece and persists changes.
 */
export function updateCreatorPortfolioPiece(creatorIdOrHandle: string, piece: PortfolioPiece): PortfolioPiece[] {
  const current = getCreatorPortfolio(creatorIdOrHandle);
  const updated = current.map((p) => (p.id === piece.id ? piece : p));
  saveCreatorPortfolio(creatorIdOrHandle, updated);
  return updated;
}

/**
 * Deletes a portfolio piece by ID and persists changes.
 */
export function deleteCreatorPortfolioPiece(creatorIdOrHandle: string, pieceId: string): PortfolioPiece[] {
  const current = getCreatorPortfolio(creatorIdOrHandle);
  const updated = current.filter((p) => p.id !== pieceId);
  saveCreatorPortfolio(creatorIdOrHandle, updated);
  return updated;
}
